/** The product list and the product form. */
const products = {
  en: {
    title: "Products",
    subtitle: "Manage your product inventory",
    add: "Add product",
    search: "Search products…",
    allCategories: "All categories",
    allSubcategories: "All subcategories",
    allJuiceTypes: "All juice types",
    allBrands: "All brands",
    loadFailed: "Failed to load products",
    deleteConfirm: "Are you sure you want to delete this product?",
    deleteFailed: "Failed to delete product",
    draft: "DRAFT",
    draftHint: "No price yet — hidden from the register and the menu until you set one",
    noPrice: "No price",
    addStock: "＋ Stock",
    stockAdded: "{name}: stock is now {count}",
    archiveConfirm:
      "“{name}” is on record in {sales} sale(s), {preorders} pre-order(s) and {deliveries} delivery(ies), so it cannot be erased without breaking that history.\n\nIt will be ARCHIVED instead: hidden from the register and the menu, history kept. You can restore it from “Archived”.\n\nContinue?",
    deleteInPack: "“{name}” is part of: {packs}. Take it out of the pack before deleting it.",
    archivedNotice: "“{name}” was archived. Its history is kept and you can restore it from “Archived”.",
    deletedNotice: "“{name}” was deleted.",
    archivedView: "🗄️ Archived",
    backToProducts: "← Products",
    archivedHint: "Deleted products that have sales, pre-orders or deliveries on record. They are hidden from the register and the menu; restoring one brings it back as it was.",
    archivedEmpty: "Nothing archived",
    restore: "↩ Restore",
    restoredNotice: "“{name}” is back in your products.",
    restoreFailed: "Failed to restore the product",
    convert: {
      button: "🗂️ Turn into a range",
      disabledHint:
        "Only a generic product can be converted. Detach it from its range first to convert this product.",
      formHint:
        "Does this product really come in several kinds (sizes, resistances, flavours)? Turn it into a range and list them. The product as saved is used; unsaved changes here are not.",
      title: "Turn into a range",
      intro:
        "“{name}” becomes a range, and you list the variants you actually have — a size, a resistance, a flavour — with the count on your shelf. Each variant is its own product, so you will see which one is running low.",
      rangeName: "Range name",
      variantsTitle: "Variants and what you have now",
      label: "Variant",
      labelPlaceholder: "e.g. 0.6Ω",
      count: "In stock",
      addRow: "+ Add another variant",
      removeRow: "Remove this variant",
      willHappen:
        "“{name}” is archived — its sales history stays, it disappears from the register and the menu — and its own stock of {stock} is dropped, because you are counting again. Variants start with its price and buy price. Offers that covered it cover the range.",
      inPacks:
        "It is part of {packs}. Let the cashier choose the variant in the pack, instead of this product. Left unticked, the conversion is refused until it is out of the pack.",
      needVariant: "List at least one variant.",
      duplicate: "“{label}” is listed twice.",
      badCount: "The count for “{label}” must be a whole number.",
      submit: "Create the range",
      submitting: "Converting…",
      done: {
        one: "“{name}” is now a range with {count} variant.",
        other: "“{name}” is now a range with {count} variants.",
      },
      failed: "Could not convert the product",
    },
    quickStock: {
      title: "Add stock",
      current: "In stock now: {count}",
      quantity: "Quantity to add",
      newTotal: "New stock: {count}",
      save: "Add to stock",
      note: "Quick add from the products list",
      failed: "Could not add the stock",
    },
    new: "NEW",
    stock: "Stock: {count}",
    stockSplit: "Shop {shop} · Warehouse {warehouse}",
    edit: "✏️ Edit",
    delete: "🗑️ Delete",
    noneFound: "No products found",
    adjustFilters: "Try adjusting your filters",
    startAdding: "Start by adding your first product",
    addFirst: "Add your first product",
    form: {
      editTitle: "Edit product",
      addTitle: "Add new product",
      saveFailed: "Failed to save product",
      name: "Product name *",
      brand: "Brand",
      category: "Category *",
      subcategory: "Subcategory",
      noSubcategory: "No subcategory",
      price: "Price ({currency}) *",
      buyPrice: "Buy price ({currency})",
      buyPricePlaceholder: "What you paid",
      youMake: "You make {amount} a unit ({margin}% margin)",
      loss: " — you are selling at a loss",
      costPrivate: "Only you see this. It is what your profit is worked out from.",
      discount: "Discount (%)",
      finalPrice: "Final price: {amount}",
      stock: "Stock quantity *",
      markNew: "Mark as new product",
      ownRecipe: "This is my own recipe",
      ownRecipeHint:
        "A juice you mix yourself. Your recipes are never shared with other shops — tick this and it stays private to you.",
      description: "Description",
      images: "Product images",
      currentImages: "Current images",
      addImages: "Add new images",
      uploadImages: "Upload images",
      imagesHint: "You can select multiple images (max 10, 5MB each)",
      newPreview: "New images preview",
      clearAll: "Clear all",
      removeImage: "Remove image",
      range: "Range",
      noRange: "Not in a range",
      rangeHint: "Put a flavour or colour in the same range as its siblings",
      variantLabel: "Variant label *",
      variantLabelPlaceholder: "e.g. Mint, Red",
      variantLabelHint: "Short name shown when the register opens the range",
      draftBanner: "This product is a draft: it has no price yet, so it is hidden from the register and the menu. Set a price and save to publish it.",
      juiceDetails: "Juice details",
      juiceType: "Juice type",
      bottleSize: "Bottle size (ml)",
      sweetness: "Sweetness (0–5): {value}",
      coldness: "Coldness (0–5): {value}",
      hardness: "Hardness (0–5): {value}",
      pg: "PG ratio (%)",
      vg: "VG ratio (%)",
      juiceTypes: {
        fruit: "Fruit",
        gourmand: "Gourmand",
        salt: "Salt nic",
      },
      saving: "Saving…",
      update: "Update product",
      create: "Create product",
    },
  },

  fr: {
    title: "Produits",
    subtitle: "Gérez votre stock de produits",
    add: "Ajouter un produit",
    search: "Rechercher un produit…",
    allCategories: "Toutes les catégories",
    allSubcategories: "Toutes les sous-catégories",
    allJuiceTypes: "Tous les types de e-liquide",
    allBrands: "Toutes les marques",
    loadFailed: "Impossible de charger les produits",
    deleteConfirm: "Voulez-vous vraiment supprimer ce produit ?",
    deleteFailed: "Impossible de supprimer le produit",
    draft: "BROUILLON",
    draftHint: "Pas encore de prix — masqué de la caisse et du menu tant que vous n'en fixez pas un",
    noPrice: "Sans prix",
    addStock: "＋ Stock",
    stockAdded: "{name} : le stock est maintenant de {count}",
    archiveConfirm:
      "« {name} » figure dans {sales} vente(s), {preorders} précommande(s) et {deliveries} livraison(s) : l'effacer casserait cet historique.\n\nIl sera donc ARCHIVÉ : masqué de la caisse et du menu, historique conservé. Vous pourrez le restaurer depuis « Archivés ».\n\nContinuer ?",
    deleteInPack: "« {name} » fait partie de : {packs}. Retirez-le du pack avant de le supprimer.",
    archivedNotice: "« {name} » a été archivé. Son historique est conservé et vous pouvez le restaurer depuis « Archivés ».",
    deletedNotice: "« {name} » a été supprimé.",
    archivedView: "🗄️ Archivés",
    backToProducts: "← Produits",
    archivedHint: "Produits supprimés qui ont des ventes, précommandes ou livraisons à leur actif. Ils sont masqués de la caisse et du menu ; les restaurer les remet tels quels.",
    archivedEmpty: "Rien d'archivé",
    restore: "↩ Restaurer",
    restoredNotice: "« {name} » est de retour dans vos produits.",
    restoreFailed: "Impossible de restaurer le produit",
    convert: {
      button: "🗂️ Transformer en gamme",
      disabledHint:
        "Seul un produit générique peut être converti. Détachez-le d'abord de sa gamme pour convertir ce produit.",
      formHint:
        "Ce produit existe-t-il vraiment en plusieurs versions (tailles, résistances, saveurs) ? Transformez-le en gamme et listez-les. Le produit tel qu'enregistré est utilisé ; les modifications non enregistrées ici ne le sont pas.",
      title: "Transformer en gamme",
      intro:
        "« {name} » devient une gamme, et vous listez les variantes que vous avez vraiment — une taille, une résistance, une saveur — avec le nombre en rayon. Chaque variante est son propre produit : vous verrez laquelle est bientôt épuisée.",
      rangeName: "Nom de la gamme",
      variantsTitle: "Les variantes et ce que vous avez maintenant",
      label: "Variante",
      labelPlaceholder: "p. ex. 0,6Ω",
      count: "En stock",
      addRow: "+ Ajouter une variante",
      removeRow: "Retirer cette variante",
      willHappen:
        "« {name} » est archivé — son historique de ventes est conservé, il disparaît de la caisse et du menu — et son propre stock de {stock} est abandonné, puisque vous recomptez. Les variantes démarrent avec son prix de vente et son prix d'achat. Les offres qui le couvraient couvrent la gamme.",
      inPacks:
        "Il fait partie de {packs}. Laisser le caissier choisir la variante dans le pack, à la place de ce produit. Décoché, la conversion est refusée tant qu'il est dans le pack.",
      needVariant: "Listez au moins une variante.",
      duplicate: "« {label} » apparaît deux fois.",
      badCount: "Le nombre pour « {label} » doit être un entier.",
      submit: "Créer la gamme",
      submitting: "Conversion…",
      done: {
        one: "« {name} » est maintenant une gamme de {count} variante.",
        other: "« {name} » est maintenant une gamme de {count} variantes.",
      },
      failed: "Impossible de convertir le produit",
    },
    quickStock: {
      title: "Ajouter du stock",
      current: "En stock actuellement : {count}",
      quantity: "Quantité à ajouter",
      newTotal: "Nouveau stock : {count}",
      save: "Ajouter au stock",
      note: "Ajout rapide depuis la liste des produits",
      failed: "Impossible d'ajouter le stock",
    },
    new: "NOUVEAU",
    stock: "Stock : {count}",
    stockSplit: "Boutique {shop} · Entrepôt {warehouse}",
    edit: "✏️ Modifier",
    delete: "🗑️ Supprimer",
    noneFound: "Aucun produit trouvé",
    adjustFilters: "Essayez de modifier vos filtres",
    startAdding: "Commencez par ajouter votre premier produit",
    addFirst: "Ajouter votre premier produit",
    form: {
      editTitle: "Modifier le produit",
      addTitle: "Nouveau produit",
      saveFailed: "Impossible d'enregistrer le produit",
      name: "Nom du produit *",
      brand: "Marque",
      category: "Catégorie *",
      subcategory: "Sous-catégorie",
      noSubcategory: "Aucune sous-catégorie",
      price: "Prix ({currency}) *",
      buyPrice: "Prix d'achat ({currency})",
      buyPricePlaceholder: "Ce que vous avez payé",
      youMake: "Vous gagnez {amount} par unité (marge de {margin} %)",
      loss: " — vous vendez à perte",
      costPrivate:
        "Vous seul voyez ceci. C'est à partir de ce prix que votre bénéfice est calculé.",
      discount: "Remise (%)",
      finalPrice: "Prix final : {amount}",
      stock: "Quantité en stock *",
      markNew: "Marquer comme nouveau produit",
      ownRecipe: "C'est ma propre recette",
      ownRecipeHint:
        "Un e-liquide que vous préparez vous-même. Vos recettes ne sont jamais partagées avec d'autres boutiques — cochez cette case et elle reste privée.",
      description: "Description",
      images: "Photos du produit",
      currentImages: "Photos actuelles",
      addImages: "Ajouter des photos",
      uploadImages: "Importer des photos",
      imagesHint:
        "Vous pouvez sélectionner plusieurs photos (10 max, 5 Mo chacune)",
      newPreview: "Aperçu des nouvelles photos",
      clearAll: "Tout retirer",
      removeImage: "Retirer la photo",
      range: "Gamme",
      noRange: "Hors gamme",
      rangeHint: "Placez une saveur ou une couleur dans la même gamme que les autres",
      variantLabel: "Nom de la variante *",
      variantLabelPlaceholder: "ex. Menthe, Rouge",
      variantLabelHint: "Nom court affiché quand la caisse ouvre la gamme",
      draftBanner: "Ce produit est un brouillon : il n'a pas encore de prix, donc il est masqué de la caisse et du menu. Fixez un prix et enregistrez pour le publier.",
      juiceDetails: "Détails de l'e-liquide",
      juiceType: "Type d'e-liquide",
      bottleSize: "Contenance (ml)",
      sweetness: "Sucré (0–5) : {value}",
      coldness: "Fraîcheur (0–5) : {value}",
      hardness: "Intensité (0–5) : {value}",
      pg: "Taux de PG (%)",
      vg: "Taux de VG (%)",
      juiceTypes: {
        fruit: "Fruité",
        gourmand: "Gourmand",
        salt: "Sel de nicotine",
      },
      saving: "Enregistrement…",
      update: "Mettre à jour",
      create: "Créer le produit",
    },
  },

  ar: {
    title: "المنتجات",
    subtitle: "إدارة مخزون منتجاتك",
    add: "إضافة منتج",
    search: "ابحث عن منتج…",
    allCategories: "كل الفئات",
    allSubcategories: "كل الفئات الفرعية",
    allJuiceTypes: "كل أنواع السوائل",
    allBrands: "كل العلامات التجارية",
    loadFailed: "تعذّر تحميل المنتجات",
    deleteConfirm: "هل أنت متأكد من حذف هذا المنتج؟",
    deleteFailed: "تعذّر حذف المنتج",
    draft: "مسودة",
    draftHint: "لا يوجد سعر بعد — مخفي عن الكاشير والقائمة حتى تحدد سعراً",
    noPrice: "بدون سعر",
    addStock: "＋ مخزون",
    stockAdded: "{name}: المخزون الآن {count}",
    archiveConfirm:
      "«{name}» مسجّل في {sales} عملية بيع و{preorders} طلب مسبق و{deliveries} توريد، وحذفه نهائياً سيفسد هذا السجل.\n\nلذلك سيتم أرشفته: يُخفى عن الكاشير والقائمة ويبقى سجله. يمكنك استرجاعه من «المؤرشف».\n\nمتابعة؟",
    deleteInPack: "«{name}» جزء من: {packs}. أخرجه من الحزمة قبل حذفه.",
    archivedNotice: "تمت أرشفة «{name}». سجله محفوظ ويمكنك استرجاعه من «المؤرشف».",
    deletedNotice: "تم حذف «{name}».",
    archivedView: "🗄️ المؤرشف",
    backToProducts: "→ المنتجات",
    archivedHint: "منتجات محذوفة لها مبيعات أو طلبات مسبقة أو توريدات مسجّلة. هي مخفية عن الكاشير والقائمة، واسترجاعها يعيدها كما كانت.",
    archivedEmpty: "لا شيء في الأرشيف",
    restore: "↩ استرجاع",
    restoredNotice: "«{name}» عاد إلى منتجاتك.",
    restoreFailed: "تعذّر استرجاع المنتج",
    convert: {
      button: "🗂️ تحويل إلى مجموعة",
      disabledHint:
        "يمكن تحويل المنتج العام فقط. افصل هذا المنتج عن مجموعته أولاً لتحويله.",
      formHint:
        "هل يأتي هذا المنتج فعلاً بأنواع عدة (مقاسات، مقاومات، نكهات)؟ حوّله إلى مجموعة واكتبها. يُستعمل المنتج كما هو محفوظ؛ أما التعديلات غير المحفوظة هنا فلا.",
      title: "تحويل إلى مجموعة",
      intro:
        "يتحول «{name}» إلى مجموعة، وتكتب أنت الأنواع التي لديك فعلاً — مقاس أو مقاومة أو نكهة — مع عددها على الرف. كل نوع منتج مستقل، فتعرف أيها يوشك على النفاد.",
      rangeName: "اسم المجموعة",
      variantsTitle: "الأنواع وما لديك الآن",
      label: "النوع",
      labelPlaceholder: "مثلاً 0.6Ω",
      count: "في المخزون",
      addRow: "+ إضافة نوع آخر",
      removeRow: "إزالة هذا النوع",
      willHappen:
        "يُؤرشف «{name}» — يبقى سجل مبيعاته ويختفي من الصندوق والقائمة — ويُهمل مخزونه الخاص وهو {stock} لأنك تعيد العدّ. تبدأ الأنواع بسعر بيعه وسعر شرائه. والعروض التي كانت تشمله تشمل المجموعة.",
      inPacks:
        "هو جزء من {packs}. اترك البائع يختار النوع داخل الباقة بدل هذا المنتج. وإن لم تحدّده يُرفض التحويل ما دام داخل الباقة.",
      needVariant: "اكتب نوعاً واحداً على الأقل.",
      duplicate: "«{label}» مكرر.",
      badCount: "عدد «{label}» يجب أن يكون عدداً صحيحاً.",
      submit: "إنشاء المجموعة",
      submitting: "جارٍ التحويل…",
      done: {
        one: "أصبح «{name}» مجموعة من نوع واحد.",
        two: "أصبح «{name}» مجموعة من نوعين.",
        few: "أصبح «{name}» مجموعة من {count} أنواع.",
        many: "أصبح «{name}» مجموعة من {count} نوعاً.",
        other: "أصبح «{name}» مجموعة من {count} نوع.",
      },
      failed: "تعذّر تحويل المنتج",
    },
    quickStock: {
      title: "إضافة مخزون",
      current: "في المخزون الآن: {count}",
      quantity: "الكمية المراد إضافتها",
      newTotal: "المخزون الجديد: {count}",
      save: "إضافة إلى المخزون",
      note: "إضافة سريعة من قائمة المنتجات",
      failed: "تعذّرت إضافة المخزون",
    },
    new: "جديد",
    stock: "المخزون: {count}",
    stockSplit: "المتجر {shop} · المخزن {warehouse}",
    edit: "✏️ تعديل",
    delete: "🗑️ حذف",
    noneFound: "لا توجد منتجات",
    adjustFilters: "جرّب تعديل عوامل التصفية",
    startAdding: "ابدأ بإضافة منتجك الأول",
    addFirst: "أضف منتجك الأول",
    form: {
      editTitle: "تعديل المنتج",
      addTitle: "إضافة منتج جديد",
      saveFailed: "تعذّر حفظ المنتج",
      name: "اسم المنتج *",
      brand: "العلامة التجارية",
      category: "الفئة *",
      subcategory: "الفئة الفرعية",
      noSubcategory: "بلا فئة فرعية",
      price: "السعر ({currency}) *",
      buyPrice: "سعر الشراء ({currency})",
      buyPricePlaceholder: "ما دفعته",
      youMake: "تربح {amount} عن كل وحدة (هامش {margin}%)",
      loss: " — أنت تبيع بخسارة",
      costPrivate: "أنت فقط ترى هذا، ومنه يُحسب ربحك.",
      discount: "الخصم (%)",
      finalPrice: "السعر النهائي: {amount}",
      stock: "الكمية في المخزون *",
      markNew: "تمييز كمنتج جديد",
      ownRecipe: "هذه وصفتي الخاصة",
      ownRecipeHint:
        "سائل تحضّره بنفسك. لا تُشارك وصفاتك مع متاجر أخرى أبداً — فعّل هذا الخيار لتبقى خاصة بك.",
      description: "الوصف",
      images: "صور المنتج",
      currentImages: "الصور الحالية",
      addImages: "إضافة صور جديدة",
      uploadImages: "رفع الصور",
      imagesHint: "يمكنك اختيار عدة صور (10 كحد أقصى، 5 ميغابايت لكل صورة)",
      newPreview: "معاينة الصور الجديدة",
      clearAll: "إزالة الكل",
      removeImage: "إزالة الصورة",
      range: "المجموعة",
      noRange: "بدون مجموعة",
      rangeHint: "ضع النكهة أو اللون ضمن نفس مجموعة أشقائه",
      variantLabel: "اسم الخيار *",
      variantLabelPlaceholder: "مثال: نعناع، أحمر",
      variantLabelHint: "اسم قصير يظهر عندما تفتح الكاشير المجموعة",
      draftBanner: "هذا المنتج مسودة: لا يوجد له سعر بعد لذلك هو مخفي عن الكاشير والقائمة. حدّد سعراً واحفظ لنشره.",
      juiceDetails: "تفاصيل السائل",
      juiceType: "نوع السائل",
      bottleSize: "حجم القارورة (مل)",
      sweetness: "الحلاوة (0–5): {value}",
      coldness: "البرودة (0–5): {value}",
      hardness: "القوة (0–5): {value}",
      pg: "نسبة PG (%)",
      vg: "نسبة VG (%)",
      juiceTypes: {
        fruit: "فواكه",
        gourmand: "غورماند",
        salt: "ملح النيكوتين",
      },
      saving: "جارٍ الحفظ…",
      update: "تحديث المنتج",
      create: "إنشاء المنتج",
    },
  },
};

export default products;
